import {Paper, Popover} from "@mantine/core";
import { Eye as IconEye, Info as IconInfoCircle } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useCallback, useMemo, useState} from "react";
import {Link} from "react-router";
import {useBHSystems, useSWCLocation} from "../../../utils/api";
import {relativeDirection} from "../../../utils/TravelPlanner";
import DirectionGrid from "./DirectionGrid";

const dir = {count: 0, hasSighting: false};
const directions = {
    N: {...dir},
    NW: {...dir},
    NE: {...dir},
    W: {...dir},
    SW: {...dir},
    SE: {...dir},
    E: {...dir},
    S: {...dir},
}

const bodyDirection = (row, field) => {
    const val = row[field];
    return <div>{val.count} ({val.directions.join(', ')})</div>
}

const bodyScore = row => {
    return row.score.toFixed(3);
};

const bodySighted = row => {
    return row.sighting ? <IconEye size={16} className="text-yellow" /> : null
}


export default function BestScansDialog({contracts}) {
    const {location} = useSWCLocation();
    const {systems} = useBHSystems();
    const {x, y} = location.coordinates.galaxy.attributes;
    const [popoverOpened, setPopoverOpened] = useState(false);
    const [selectedScan, setSelectedScan] = useState(null);

    const bodyName = useCallback(row => {
        return <Link className="contract-name" to={`/bh/contract/${row.id}`} state={{prev: 'heatmap'}}>{row.name}</Link>;
    }, []);

    const bestScans = useMemo(() => {
        if(contracts === null || systems.length === 0) {
            return [];
        }
        return contracts.filter(c => c.locations.length > 1 && !c.scans.some(l => l.x === x && l.y === y)).map(c => {
            const map = structuredClone(directions);

            for (let loc of c.locations) {
                const sys = systems.find(s => s.id === loc.id);
                const dir = relativeDirection(x, y, sys.galX, sys.galY);
                map[dir].count += 1;
            }
            if(c.sighting && c.sighting.location.system) {
                const dir = relativeDirection(x, y, c.sighting.location.system.galX, c.sighting.location.system.galY);
                map[dir].hasSighting = true;
            }

            let max = {count: -1, directions: []};
            let min = {count: Infinity, directions: []};

            for (const [direction, {count}] of Object.entries(map)) {
                if (count > max.count) {
                    max = {count, directions: [direction]};
                } else if (count === max.count) {
                    max.directions.push(direction);
                }

                if (count < min.count && count > 0) {
                    min = {count, directions: [direction]};
                } else if (count === min.count) {
                    min.directions.push(direction);
                }
            }

            const score = max.count === 1 ? 0 : max.count / c.locations.length;


            return {
                ...c,
                min,
                max,
                map,
                score
            };
        }).sort((a, b) => a.score - b.score);
    }, [contracts, location]);

    const bodyMap = useCallback(row => {
        return <Popover opened={popoverOpened && selectedScan === row.map} onChange={setPopoverOpened}>
            <Popover.Target>
                <IconInfoCircle
                    size={16}
                    className="clickable text-blue"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedScan(row.map);
                        setPopoverOpened(true);
                    }}
                />
            </Popover.Target>
            <Popover.Dropdown>
                <DirectionGrid data={row.map}/>
            </Popover.Dropdown>
        </Popover>
    }, [popoverOpened, selectedScan]);

    const locationName = useMemo(() => {
        if(location.system.value) {
            return location.system.value;
        } else {
            return `Deep Space (${location.coordinates.galaxy.attributes.x}, ${location.coordinates.galaxy.attributes.y})`;
        }
    }, [location])

    return (
        <Paper className="mt-4">
            <h3 className="mt-0">{`Best Scans from ${locationName}`}</h3>
            <DataTable
                records={bestScans}
                idAccessor="id"
                columns={[
                    {accessor: 'name', title: 'Contract', render: bodyName},
                    {accessor: 'sighted', title: '', render: bodySighted},
                    {accessor: 'min', title: 'Min', render: (row) => bodyDirection(row, 'min')},
                    {accessor: 'max', title: 'Max', render: (row) => bodyDirection(row, 'max')},
                    {accessor: 'score', title: 'Score', render: bodyScore},
                    {accessor: 'map', title: 'Map', render: bodyMap},
                ]}
            />
            <p><i>Note: in this case a lower score is better. 0 means you will confirm the location of the target with the scan, while 1 means no information will be gained</i></p>
        </Paper>
    )
}
